import * as React from 'react';
import { useState } from 'react';
import { TextField, Typography, Grid, Box } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { ShopPublicInfo } from 'common/types';
import { ReduxState } from 'services/types';
import { connect } from 'react-redux';
import ContainerBox from 'components/ContainerBox';
import TimeZoneChangeDialog from 'components/TimeZoneChangeDialog';
import { getCountryTimeZones, getAllTimeZones, getFormattedTimeZone } from 'common/utils/dateUtils';
import { useTranslation } from 'services/localization/useTranslation';
import { getCountryTimeZone } from 'common/utils/shopUtils';
import AutoCompleteCountryPicker from 'common/components/pickers/AutoCompleteCountryPicker';
import Banner from 'components/Banner';

interface StateToProps {
	userEmail: string;
}

interface OwnProps {
	publicInfo: ShopPublicInfo;
	setPublicInfo: (publicInfo: ShopPublicInfo) => void;
}

type Props = StateToProps & OwnProps;

const TimeZone = (props: Props) => {
	const publicInfo = props.publicInfo;
	const [isConfirmationOpen, setIsConfirmationOpen] = useState<boolean>(false);
	const [timeZone, setTimeZone] = useState<string>(publicInfo.timeZone);
	const [newTimeZone, setNewTimeZone] = useState<string>('');
	const [selectedCountryCode, setSelectedCountryCode] = useState(publicInfo.country || 'FI');
	const [countryTimeZones, setCountryTimeZones] = useState(
		getCountryTimeZones(publicInfo.country || 'FI'),
	);
	const [modalTypeCounty, setModalTypeCountry] = useState<boolean>(false);

	const { t } = useTranslation();
	const classes = useStyles();

	const allTimeZones = getAllTimeZones();

	const allTimeZoneOptions = Object.keys(allTimeZones).map((keyName) => {
		const timeZone = allTimeZones[keyName];
		return (
			<option key={timeZone.name} value={timeZone.name}>
				{getFormattedTimeZone(timeZone)}
			</option>
		);
	});

	const countryTimeZoneOptions = countryTimeZones.map((timeZone) => {
		return (
			<option key={timeZone.name} value={timeZone.name}>
				{getFormattedTimeZone(timeZone)}
			</option>
		);
	});

	const handleTimezoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setNewTimeZone(e.target.value);
		setModalTypeCountry(false);
		setIsConfirmationOpen(true);
	};

	const handleSaveTimezoneChange = () => {
		setIsConfirmationOpen(false);
		props.setPublicInfo({
			...publicInfo,
			timeZone: newTimeZone,
		});
		setTimeZone(newTimeZone);
		setNewTimeZone('');
	};

	const countryAndTimeZoneAdditionalText = () => {
		return (
			<Grid item>
				{countryTimeZones.length === 1 ? (
					<Typography className={classes.countryString}>
						{t('shop.newTimeZone', 'New time zone:') +
							' ' +
							getFormattedTimeZone(countryTimeZones[0]) +
							'.'}
					</Typography>
				) : (
					<Typography className={classes.countryString}>
						{t('shop.newTimeZone', 'New time zone:')}
					</Typography>
				)}

				{countryTimeZones.length > 1 && (
					<TextField
						variant="standard"
						select
						name="timeZone"
						value={newTimeZone}
						disabled={countryTimeZones.length <= 1}
						className={classes.timeZoneSelector}
						onChange={(e) => setNewTimeZone(e.target.value)}
						SelectProps={{
							native: true,
						}}
						fullWidth
					>
						{countryTimeZoneOptions}
					</TextField>
				)}
				{timeZoneAlert()}
			</Grid>
		);
	};

	const handleCountryCodeChange = (_countryCode: string | React.ChangeEvent<HTMLInputElement>) => {
		const countryCode = typeof _countryCode === 'string' ? _countryCode : _countryCode.target.value;

		const updatedTimeZone = getCountryTimeZone(countryCode);
		if (updatedTimeZone !== publicInfo.timeZone) {
			const updatedCountryTimeZones = getCountryTimeZones(countryCode);
			setCountryTimeZones(updatedCountryTimeZones);
			setNewTimeZone(updatedTimeZone);
			setModalTypeCountry(true);
			setIsConfirmationOpen(true);
		}

		setSelectedCountryCode(countryCode);
		props.setPublicInfo({ ...publicInfo, country: countryCode });
	};

	const timeZoneAlert = () => (
		<Banner
			mt={2}
			variant="warning"
			title={t(
				'shop.changingTimezoneWillUpdate',
				'After changing your timezone, the times of current and old orders might display incorrectly since they are displayed relative to the new timezone.',
			)}
			description={t(
				'shop.weDoNotReccomendTheChange',
				'We do not recommend the change if your shop has processed live orders. Please be in touch with our support if you need to change the timezone nevertheless.',
			)}
		/>
	);

	return (
		<ContainerBox>
			<Grid container spacing={2}>
				<Grid item xs={12} md={6}>
					<Typography variant="h5">{t('shop.timezone')}</Typography>
				</Grid>
				<Grid item xs={12} md={6}>
					<AutoCompleteCountryPicker
						label={t('common:form.country', 'Country')}
						id="country-select"
						value={selectedCountryCode}
						onChange={handleCountryCodeChange}
						variant="outlined"
					/>
					<Box mt={4} />
					<TextField
						variant="standard"
						select
						name="timeZone"
						value={timeZone}
						label={t('shop.timezone', 'Time zone')}
						onChange={handleTimezoneChange}
						SelectProps={{
							native: true,
						}}
						fullWidth
					>
						{allTimeZoneOptions}
					</TextField>
				</Grid>
			</Grid>
			<TimeZoneChangeDialog
				open={isConfirmationOpen}
				header={
					modalTypeCounty
						? t('shop.changeAlsoTimeZone', 'Change the time zone at the same time?')
						: t('shop.changeTimeZone', 'Change time zone?')
				}
				checkBoxLabel={t(
					'shop.understandConsequencesOfTimeZoneChange',
					'I understand the risks stated above, and want to change my shop timezone.',
				)}
				additionalText={modalTypeCounty ? countryAndTimeZoneAdditionalText() : timeZoneAlert()}
				cancelButton={
					modalTypeCounty
						? t('shop.noThankYou', 'No thank you')
						: t('shop.dontChange', `Don't change`)
				}
				acceptButton={t('shop.yesChange', 'Yes, change time zone')}
				onCancel={() => setIsConfirmationOpen(false)}
				onAccept={handleSaveTimezoneChange}
			/>
		</ContainerBox>
	);
};

const mapStateToProps = ({ user }: ReduxState): StateToProps => {
	const userEmail = user.user.data?.email ?? '';
	return { userEmail };
};

const useStyles = makeStyles((theme) => ({
	timeZoneSelector: {
		marginTop: theme.spacing(1),
		marginBottom: theme.spacing(2),
	},
	countryString: {
		marginBottom: theme.spacing(2),
	},
}));

export default connect(mapStateToProps, {})(TimeZone);
